import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

import { fetchTeamStandings } from './StandingsApi.jsx';

const getTeamPhoto = (team) => {
  if (team?.logoUrl) return team.logoUrl;
  return '';
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;

  const teamLabel = team?.name || team?.fullName || team?._id;
  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatInteger = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
  }).format(value);
};

export default function StandingsSection({
  provider,
  discipline,
  competitionIds = [],
  season,
  day = null,
  statType = 'general',
  onNavigate,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    items: [],
  });

  const competitionKey = useMemo(
    () => competitionIds?.join(',') || '',
    [competitionIds]
  );

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const result = await fetchTeamStandings({
          provider,
          discipline,
          competitionIds,
          season,
          day,
          statType,
        });

        if (!ignore) {
          setData({
            items: Array.isArray(result?.items) ? result.items : [],
          });
        }
      } catch (e) {
        if (!ignore) {
          setError('Impossible de charger le classement.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [provider, discipline, competitionKey, season, day, statType]);

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  const renderDesktopTable = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 52 }}>#</TableCell>
            <TableCell>Équipe</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Pts</TableCell>
            <TableCell align="right">MJ</TableCell>
            <TableCell align="right">G</TableCell>
            <TableCell align="right">N</TableCell>
            <TableCell align="right">P</TableCell>
            {discipline === 'rugby' ? (
              <>
                <TableCell align="right">BO</TableCell>
                <TableCell align="right">BD</TableCell>
              </>
            ) : null}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.items.map((item, index) => {
            const teamUrl = buildTeamUrl(item.team);

            return (
              <TableRow key={item.team?._id || `${statType}-${index}`} hover>
                <TableCell sx={{ fontWeight: 700 }}>
                  {item.rank ?? index + 1}
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={getTeamPhoto(item.team)}
                      alt={item.team?.name || 'team'}
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: 'grey.200',
                        overflow: 'hidden',
                        '& .MuiAvatar-img': {
                          objectFit: 'cover',
                          objectPosition: 'center',
                          transform: 'scale(1.15)',
                        },
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {teamUrl ? (
                          <Link
                            href={teamUrl}
                            underline="hover"
                            color="inherit"
                            onClick={(e) => handleLinkClick(e, teamUrl)}
                          >
                            {item.team?.name || item.team?.fullName || '-'}
                          </Link>
                        ) : (
                          item.team?.name || item.team?.fullName || '-'
                        )}
                      </Typography>

                      {item.team?.fullName &&
                      item.team?.fullName !== item.team?.name ? (
                        <Typography variant="caption" color="text.secondary">
                          {item.team.fullName}
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatInteger(item.points)}
                </TableCell>
                <TableCell align="right">{formatInteger(item.played)}</TableCell>
                <TableCell align="right">{formatInteger(item.won)}</TableCell>
                <TableCell align="right">{formatInteger(item.draw)}</TableCell>
                <TableCell align="right">{formatInteger(item.lost)}</TableCell>

                {discipline === 'rugby' ? (
                  <>
                    <TableCell align="right">{formatInteger(item.BO)}</TableCell>
                    <TableCell align="right">{formatInteger(item.BD)}</TableCell>
                  </>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Stack spacing={1.25}>
      {data.items.map((item, index) => {
        const teamUrl = buildTeamUrl(item.team);

        return (
          <Card
            key={item.team?._id || `${statType}-${index}`}
            variant="outlined"
            sx={{ borderRadius: 2.5, borderColor: '#e5e7eb' }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 20, fontWeight: 800 }}
                  >
                    {item.rank ?? index + 1}
                  </Typography>

                  <Avatar
                    src={getTeamPhoto(item.team)}
                    alt={item.team?.name || 'team'}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'grey.200',
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {teamUrl ? (
                        <Link
                          href={teamUrl}
                          underline="hover"
                          color="inherit"
                          onClick={(e) => handleLinkClick(e, teamUrl)}
                        >
                          {item.team?.name || item.team?.fullName || '-'}
                        </Link>
                      ) : (
                        item.team?.name || item.team?.fullName || '-'
                      )}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {formatInteger(item.points)} pts
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="caption">MJ {formatInteger(item.played)}</Typography>
                  <Typography variant="caption">G {formatInteger(item.won)}</Typography>
                  <Typography variant="caption">N {formatInteger(item.draw)}</Typography>
                  <Typography variant="caption">P {formatInteger(item.lost)}</Typography>
                  {discipline === 'rugby' ? (
                    <>
                      <Typography variant="caption">BO {formatInteger(item.BO)}</Typography>
                      <Typography variant="caption">BD {formatInteger(item.BD)}</Typography>
                    </>
                  ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data.items.length) {
    return (
      <Alert severity="info">
        Aucun classement disponible pour cette sélection.
      </Alert>
    );
  }

  return isMobile ? renderMobileCards() : renderDesktopTable();
}